import React from 'react';
import { assignmentApi } from '../api/assignmentApi';
import type { BranchRule, BranchRuleInput, SurveyAssignedQuestion } from '../types';

export function useBranching(
  questionId: number,
  surveyId: number,
): {
  rules: BranchRule[];
  eligibleTargets: SurveyAssignedQuestion[];
  isLoading: boolean;
  isSaving: boolean;
  error: Error | null;
  saveRules: (rows: BranchRuleInput[]) => Promise<void>;
} {
  const [rules, setRules] = React.useState<BranchRule[]>([]);
  const [eligibleTargets, setEligibleTargets] = React.useState<SurveyAssignedQuestion[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    if (!surveyId) return;
    let cancelled = false;
    setIsLoading(true);

    assignmentApi
      .getBranching(questionId, surveyId)
      .then((ctx) => {
        if (!cancelled) {
          setRules(ctx.rules);
          setEligibleTargets(ctx.eligibleTargets);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [questionId, surveyId]);

  const saveRules = React.useCallback(
    async (rows: BranchRuleInput[]) => {
      setIsSaving(true);
      try {
        await assignmentApi.saveBranching(questionId, surveyId, rows);
        // Refresh after save
        const ctx = await assignmentApi.getBranching(questionId, surveyId);
        setRules(ctx.rules);
        setEligibleTargets(ctx.eligibleTargets);
      } finally {
        setIsSaving(false);
      }
    },
    [questionId, surveyId],
  );

  return { rules, eligibleTargets, isLoading, isSaving, error, saveRules };
}
